import { useCallback, useEffect, useRef, useState } from "react";

type UseStorageListenerProps = {
    decoder?: (value?: string|null) => any;
    defaultValue?: any;
    encoder?: (value: any) => string;
    storageKey: string;
};

const useStorageListener = ({
    decoder = v => v,
    defaultValue,
    encoder = v => String(v),
    storageKey,
}: UseStorageListenerProps) => {
    const [value, setValue] = useState(defaultValue);
    const event = `storageUpdated:${storageKey}`;
    const initialized = useRef<boolean>(false);

    const readValue = useCallback(() => {
        if (!window.localStorage) {
            return null;
        }
        const storedValue = window.localStorage.getItem(storageKey);
        if (storedValue === null || !storedValue?.length) {
            return null;
        }
        try {
            return decoder(storedValue);
        } catch (e) {
            console.error("Error reading from localStorage", e);
            return null;
        }
    }, [decoder, storageKey]);

    const storeValue = useCallback((newValue: any) => {
        try {
            const serializedValue = encoder(newValue);
            window?.localStorage?.setItem(storageKey, serializedValue);
            window.dispatchEvent(new CustomEvent(event));
        } catch (e) {
            console.error("Error writing to localStorage", e);
        }
    }, [encoder, event, storageKey]);

    useEffect(() => {
        const handleStorageUpdated = () => {
            const nextValue = readValue();
            setValue(nextValue);
        };

        if (!initialized.current) {
            initialized.current = true;
            const initialValue = readValue();
            if (!initialValue && defaultValue !== undefined) {
                window?.localStorage?.setItem(storageKey, defaultValue);
            } else if (initialValue !== defaultValue) {
                setValue(initialValue);
            }
        }

        window.addEventListener(event, handleStorageUpdated);
        window.addEventListener("storage", handleStorageUpdated);

        return () => {
            window.removeEventListener(event, handleStorageUpdated);
            window.removeEventListener("storage", handleStorageUpdated);
        };
    }, [defaultValue, event, readValue, storageKey]);

    return { storeValue, value };
};

export { useStorageListener as default, useStorageListener };
export type { UseStorageListenerProps };
